import { Fragment, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import styled from '@emotion/styled';
import { Colors, Shadows, Typography } from '@zapier/design-system';
import { readCookie } from '@zapier/cookies';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import {
  CTAType,
  EventAction,
  EventName,
  InteractionName,
  useAvroTracking,
  useTrackEvent,
} from '../../observability';
import { unsecureID } from '../../utils';

declare const hbspt: {
  forms: {
    create: (createArgs: {
      formId: string;
      portalId: string;
      target: string;
      onFormReady: (input: any) => void;
    }) => any;
  };
};

export type HubSpotFormProps = {
  description?: string;
  hubspotFormId: string;
  hubspotPortalId: string;
  interaction_raw_text?: string;
  prefilledFields?: {
    name: string;
    value?: string;
    cookieToBind?: string;
  }[];
  title: string;
};

export function HubSpotForm({
  description,
  hubspotFormId,
  hubspotPortalId,
  interaction_raw_text,
  prefilledFields,
  title,
}: HubSpotFormProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerID] = useState(unsecureID('hubspot-form-'));
  const trackEvent = useTrackEvent();

  const { emitUserInteractionEvent } = useAvroTracking();

  useEffect(() => {
    // https://legacydocs.hubspot.com/global-form-events
    const handleFormCallback = (event: MessageEvent) => {
      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormSubmit'
      ) {
        emitUserInteractionEvent({
          event_action: EventAction.click,
          interaction_name: InteractionName.call_to_action,
          interaction_raw_text: interaction_raw_text || title,
          option_selected: CTAType.cta_button,
          custom_tracking_fields: {
            section_name: 'HubSpotForm',
            hubspot_form_id: hubspotFormId,
            hubspot_portal_id: hubspotPortalId,
          },
        });
        trackEvent(EventName.HUBSPOT_FORM_SUBMITTED, {
          title,
          hubspot_form_id: hubspotFormId,
          hubspot_portal_id: hubspotPortalId,
          path: window.location.pathname,
        });
      }
    };

    window.addEventListener('message', handleFormCallback);

    return () => {
      window.removeEventListener('message', handleFormCallback);
    };

    // Including `trackEvent` as a dependency causes this effect to be run much more than needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubspotFormId, hubspotPortalId, title]);

  const mountForm = () => {
    if (
      typeof hbspt === 'undefined' ||
      !containerRef.current ||
      containerRef.current.dataset.mounted === 'true'
    ) {
      return;
    }

    function prefillFields($form: any[]) {
      prefilledFields?.forEach(({ name, value, cookieToBind }) => {
        const input = $form[0].querySelector(`input[name=${name}]`);
        input.value =
          cookieToBind && cookieToBind.trim() !== ''
            ? readCookie(cookieToBind)
            : value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    hbspt.forms.create({
      formId: hubspotFormId,
      portalId: hubspotPortalId,
      target: `#${containerRef.current.id}`,
      onFormReady: prefillFields,
    });

    containerRef.current.dataset.mounted = 'true';
  };

  return (
    <Fragment>
      <Script src="https://js.hsforms.net/forms/v2.js" onReady={mountForm} />
      {/* We have to load jQuery to be able to use onFormReacy in hbspt.forms.create :-( */}
      {!!prefilledFields?.length && (
        <Script src="https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js" />
      )}
      <Container>
        {title && <Title>{title}</Title>}
        {description && (
          <Description>
            <RichTextHtml html={description} />
          </Description>
        )}
        <Form id={containerID} ref={containerRef} data-mounted="false" />
      </Container>
    </Fragment>
  );
}

const Container = styled.div`
  max-width: 570px;
  margin: 0 auto;
  padding: 35px 30px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid ${Colors.gray20};
  box-shadow: ${Shadows.elevation5};
  background-color: ${Colors.white};
`;

const Form = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
`;

const Title = styled.h3`
  margin-bottom: 10px;
  text-align: center;
  color: ${Colors.gray};
  ${Typography.sectionHeader3Extrabold}
`;

const Description = styled.div`
  margin-bottom: 30px;
  text-align: center;
  color: ${Colors.gray80};
  ${Typography.smallPrint1}
`;
